import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { sponsorsAPI } from '../utils/api';

/**
 * 설문·자문단 패널 (우측 사이드바, 검증 의사 전용).
 * 참여 가능한 유료 서베이 목록 + 응답 모달 — 명시적 동의 체크 없이는 제출 불가.
 */
export default function SurveyPanel({ specialty }: { specialty?: string }) {
  const [surveys, setSurveys] = useState<any[]>([]);
  const [open, setOpen] = useState<any>(null); // 응답 중인 서베이
  const [answers, setAnswers] = useState<string[]>([]);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    sponsorsAPI.availableSurveys(specialty)
      .then(r => setSurveys(r.data.surveys || []))
      .catch(() => setSurveys([]));
  };
  useEffect(load, [specialty]);

  const start = (s: any) => {
    setOpen(s);
    setAnswers(new Array((s.questions || []).length).fill(''));
    setConsent(false);
    setErr('');
  };

  const submit = async () => {
    if (!open) return;
    if (!consent) { setErr('안내 사항에 동의해야 참여할 수 있습니다.'); return; }
    setSubmitting(true);
    setErr('');
    try {
      await sponsorsAPI.respondSurvey(open.id, answers, true);
      setOpen(null);
      load();
    } catch (e: any) {
      setErr(e?.response?.data?.error || '제출에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (surveys.length === 0 && !open) return null;

  return (
    <div className="mb-6">
      <h3 className="font-semibold text-gray-900 mb-3">설문·자문단</h3>
      <div className="space-y-2">
        {surveys.map(s => (
          <button
            key={s.id}
            onClick={() => start(s)}
            className="w-full text-left border border-gray-200 rounded-xl p-3 hover:border-gray-300 transition-colors"
          >
            <p className="text-sm font-medium text-gray-900">{s.title}</p>
            {(s.reward_text ?? s.rewardText) && (
              <p className="text-xs text-gray-500 mt-0.5">{s.reward_text ?? s.rewardText}</p>
            )}
            <p className="text-xs text-gray-400 mt-0.5">{s.sponsor_name ?? s.sponsorName} · 유료 설문</p>
          </button>
        ))}
      </div>

      {/* 응답 모달 */}
      {open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[70] flex items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-semibold text-gray-900">{open.title}</h3>
              <button onClick={() => setOpen(null)} className="p-1 text-gray-400 hover:text-gray-600 rounded"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-400 mb-1">{open.sponsor_name ?? open.sponsorName} 제공 · 유료 설문</p>
            {open.description && <p className="text-sm text-gray-600 mb-2">{open.description}</p>}
            {(open.reward_text ?? open.rewardText) && (
              <p className="text-sm text-gray-900 border border-gray-200 rounded-lg px-3 py-2 mb-4">사례비: {open.reward_text ?? open.rewardText}</p>
            )}

            <div className="space-y-4">
              {(open.questions || []).map((q: any, i: number) => (
                <div key={i}>
                  <p className="text-sm font-medium text-gray-900 mb-1.5">{i + 1}. {q.text}</p>
                  {q.options ? (
                    <div className="space-y-1">
                      {q.options.map((opt: string) => (
                        <label key={opt} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                          <input
                            type="radio"
                            name={`q${i}`}
                            checked={answers[i] === opt}
                            onChange={() => setAnswers(a => a.map((v, j) => (j === i ? opt : v)))}
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      value={answers[i] || ''}
                      onChange={e => setAnswers(a => a.map((v, j) => (j === i ? e.target.value : v)))}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>

            <label className="flex items-start gap-2 mt-5 text-xs text-gray-600 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" />
              <span>
                응답이 스폰서에게 <strong className="text-gray-900">익명·집계 형태</strong>로 제공되는 것과
                사례비 지급 관련 안내(관련 법규·공정경쟁규약 준수)에 동의합니다.
              </span>
            </label>

            {err && <p className="text-sm text-red-600 mt-3">{err}</p>}

            <button
              onClick={submit}
              disabled={submitting || !consent || answers.some(a => !String(a).trim())}
              className="mt-4 w-full px-4 py-2.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm font-medium"
            >
              {submitting ? '제출 중…' : '동의하고 제출'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
